import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Activity, ShieldCheck, Microscope, Database, Scaling, Globe, Zap, HeartPulse, Check } from 'lucide-react';
import FaqSection from '../components/FaqSection';


const StatCard: React.FC<{ value: string; label: string; link?: string }> = ({ value, label, link }) => {
  const trimmed = link ? link.trim() : '';
  return (
    <motion.div 
      whileHover={{ y: -3 }}
      className="glass-card p-6 rounded-3xl text-center transition-theme border border-[var(--card-border)]"
    >
      <h3 className="text-3xl font-bold text-[var(--accent-blue)] mb-1">{value}</h3>
      <p className="text-[var(--text-muted)] text-xs uppercase tracking-wider font-semibold">{label}</p>
      {trimmed && (
        <p className="text-xs mt-2">
          <a href={trimmed} target="_blank" rel="noopener noreferrer" className="text-[var(--accent-blue)] font-semibold underline" aria-label={`Open source for ${label}`}>
            View source article
          </a>
        </p>
      )}
    </motion.div>
  );
};

const Home: React.FC = () => {
  return (
    <div className="space-y-24">
      {/* Hero Section */}
      <section id="hero" className="w-full relative h-screen flex items-center overflow-hidden bg-black">
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
            preload="metadata"
            poster="https://res.cloudinary.com/dkfrelnfq/video/upload/q_auto,f_auto,so_0/v1/Copy_of_Every_drop_of_blood_holds_a_universe_of_information_1_fya1ua.jpg"
          >
            <source src="https://res.cloudinary.com/dkfrelnfq/video/upload/q_auto,f_auto/v1/Copy_of_Every_drop_of_blood_holds_a_universe_of_information_1_fya1ua.mp4" type="video/mp4" />
          </video>
        </div>
        
        <div className="container mx-auto px-6 max-w-6xl relative z-10">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* <span className="inline-block px-4 py-1.5 rounded-full bg-[var(--accent-blue-soft)] text-[var(--accent-blue)] text-xs font-bold mb-6 border border-[var(--accent-blue)]/20 transition-theme shadow-sm uppercase tracking-widest">
              Next-Gen Optical Sensing
            </span> */}
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white tracking-tight leading-[1.1] mb-8 drop-shadow-lg">
              PreciQube <br />
              <span className="text-[50px] text-transparent bg-clip-text bg-gradient-to-r from-[#6366f1] to-[#a5b4fc]">
                Quantum enabled early Cancer detection
              </span>
            </h1>
            <p className="text-base md:text-lg text-white/90 max-w-2xl mx-auto mb-10 leading-relaxed drop-shadow-md">
              We are developing next-generation diagnostic systems that utilise quantum-enhanced optical 
              sensing to enable earlier, safer and affordable disease detection across India, beginning with cancer.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="px-8 py-4 bg-[var(--accent-blue)] text-white rounded-2xl font-semibold shadow-xl shadow-blue-500/20 hover:brightness-110 transition-all flex items-center justify-center gap-2">
                Connect With Us <ArrowRight className="w-4 h-4" />
              </Link>
              {/* <Link to="/solutions" className="px-8 py-4 bg-[var(--card-bg)] backdrop-blur text-[var(--text-main)] border border-[var(--card-border)] rounded-2xl font-semibold hover:bg-[var(--accent-blue-soft)] transition-all flex items-center justify-center">
                View Product Range
              </Link> */}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Container */}
      <div className="container mx-auto px-6 max-w-6xl space-y-24">
        {/* Problem Section */}
        <section id="problem" className="space-y-12">
          <div className="text-center">
            <h2 className="text-3xl font-bold mb-3 text-[var(--text-main)] transition-theme">India's Cancer Screening Infrastructure Crisis</h2>
            <p className="text-[var(--text-muted)] text-sm transition-theme">Early detection remains inaccessible for millions!</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
  <StatCard 
    value="15L+" 
    label="New cancer cases annually" 
    link=" https://pmc.ncbi.nlm.nih.gov/articles/PMC10231735/"
  />
  <StatCard 
    value="9L+" 
    label="Annual deaths from treatable cancers" 
    link="https://gco.iarc.who.int/media/globocan/factsheets/populations/356-india-fact-sheet.pdf"
  />
  <StatCard 
    value="<2%" 
    label="Screening coverage in high-risk groups" 
    link="https://pmc.ncbi.nlm.nih.gov/articles/PMC11610822/"
  />
</div>

          {/* <div className="glass-card rounded-[2rem] overflow-hidden transition-theme border border-[var(--card-border)]">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-[var(--accent-blue-soft)]/20 border-b border-[var(--card-border)]">
                    <th className="p-5 font-bold text-xs uppercase tracking-wider text-[var(--text-main)]">Screening Type</th>
                    <th className="p-5 font-bold text-xs uppercase tracking-wider text-[var(--text-main)]">Installed Capacity</th>
                    <th className="p-5 font-bold text-xs uppercase tracking-wider text-[var(--text-main)]">Required Coverage</th>
                    <th className="p-5 font-bold text-xs uppercase tracking-wider text-[var(--text-main)]">Gap</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--card-border)]">
                  {[
                    { type: "Mammography / thermal units", capacity: "2,000–3,000", required: "> 20,000", gap: "8-10× shortage" },
                    { type: "HPV molecular laboratories", capacity: "20–30", required: "> 100", gap: "3-5× shortage" },
                    { type: "Trained cytotechnicians", capacity: "< 5,000", required: "> 25,000", gap: "5× shortage" }
                  ].map((row, i) => (
                    <tr key={i} className="hover:bg-[var(--accent-blue-soft)]/5 transition-colors">
                      <td className="p-5 text-sm font-medium text-[var(--text-main)]">{row.type}</td>
                      <td className="p-5 text-sm text-[var(--text-muted)]">{row.capacity}</td>
                      <td className="p-5 text-sm text-[var(--text-muted)]">{row.required}</td>
                      <td className="p-5 text-sm font-bold text-[#f03e3e]">{row.gap}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div> */}

          {/* Post-Table Problem Context - Tightened */}
          <div className="max-w-4xl mx-auto space-y-10">
            <p className="text-base md:text-lg text-[var(--text-muted)] leading-relaxed font-normal transition-theme">
              Despite advances in medicine, every year, over <span className="text-[var(--accent-blue)]">0.9+ million</span> people die from cancer, mainly due to late-stage detection, where significant disease progression limits the effectiveness of treatment
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                "Invasive and costly",
                "Infrastructure-heavy",
                "Unsuitable for large-scale, routine screening"
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-4 glass-card rounded-2xl border border-[var(--card-border)] transition-theme">
                  <div className="w-8 h-8 rounded-lg bg-[var(--accent-blue-soft)] flex items-center justify-center text-[var(--accent-blue)]">
                    <Activity size={16} />
                  </div>
                  <span className="text-xs font-bold text-[var(--text-main)] uppercase tracking-tight">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-sm text-center text-[var(--text-muted)] italic leading-relaxed px-10">
              In India and many resource-constrained settings, limited screening infrastructure and a shortage of skilled personnel further widen this gap.
            </p>
          </div>
        </section>

        {/* Vision Section - More Compact */}
        <section id="vision" className="space-y-8 py-10">
          <div className="text-center space-y-2">
            <h2 className="text-xs font-bold uppercase tracking-[0.3em] text-[var(--accent-blue)]">Our Vision</h2>
            <h3 className="text-3xl font-bold text-[var(--text-main)]">Early detection, without compromise</h3>
          </div>
          
          <div className="max-w-4xl mx-auto space-y-8">
            <p className="text-sm text-center text-[var(--text-muted)] font-medium">
              We envision a world where early disease detection is:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                "Non-invasive", 
                "Label-free", 
                "Cost-effective", 
                "Safe for repeated use", 
                "Deployable at scale"
              ].map((v, i) => (
                <div key={i} className="glass-card p-4 rounded-2xl text-center border border-[var(--card-border)] group hover:border-[var(--accent-blue)] transition-colors">
                  <p className="text-[11px] font-bold text-[var(--text-main)] uppercase tracking-wider">{v}</p>
                </div>
              ))}
            </div>
            <p className="text-sm text-center text-[var(--accent-blue)] font-bold italic">
              Quantum technologies now make this vision technically and commercially achievable.
            </p>
          </div>
        </section>

        {/* Approach Section - Refined Grid */}
        <section id="approach" className="glass-card rounded-[3rem] p-8 md:p-16 transition-theme border border-[var(--accent-blue)]/20 shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-[var(--accent-blue)]/5 blur-[80px] rounded-full pointer-events-none" />
          
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-5">
              <h2 className="text-xs font-bold uppercase tracking-[0.3em] text-[var(--accent-blue)]">Our Approach</h2>
              <h3 className="text-3xl font-bold text-[var(--text-main)]">Quantum-enhanced Optical Diagnostics</h3>
              <p className="text-base text-[var(--text-muted)] leading-relaxed">
                PreciQube integrates Quantum Optics to detect biochemical signals that are otherwise buried under noise.
              </p>
            </div>
            
            <div className="space-y-4">
              <p className="text-[10px] font-bold text-[var(--text-main)] uppercase tracking-widest opacity-50">This enables:</p>
              {[
                "Detection of Ultra-weak molecular signatures",
                "Earlier-stage disease identification",
                "Reliable, Repeatable measurements without reagents or radiation exposure"
              ].map((item, i) => (
                <div key={i} className="flex gap-4 p-4 glass-card rounded-xl border border-[var(--card-border)] bg-white/5 items-center">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-[var(--accent-blue)] flex items-center justify-center text-white">
                    <Check size={12} />
                  </div>
                  <span className="text-sm font-semibold text-[var(--text-main)]">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <FaqSection fullBleed className="" />

        {/* Call to Action Section - Balanced Padding */}
        <section id="cta" className="bg-[var(--footer-bg)] rounded-[3rem] p-12 md:p-20 text-center text-white relative overflow-hidden shadow-2xl">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-[100px] -mr-48 -mt-48" />
          
          <div className="relative z-10 max-w-4xl mx-auto space-y-10">
            <div className="space-y-3">
              <h2 className="text-xs font-bold uppercase tracking-[0.4em] text-[var(--accent-blue)]">Call to Action</h2>
              <h3 className="text-4xl font-bold tracking-tight">Building the Future of Diagnostics</h3>
            </div>
            
            <div className="space-y-6 max-w-2xl mx-auto">
              <p className="text-white/80 text-base leading-relaxed">
                We are in the early build phase, manufacturing core optical components, securing intellectual property, and validating design choices through continuous engagement with clinical and industry stakeholders.
              </p>
              <p className="text-white/80 text-base leading-relaxed">
                If you are interested in collaborating, advising, or exploring future applications, we are open to technical discussions, pilot collaborations, and long-term partnerships.
              </p>
            </div>

            <div className="pt-4">
              <Link to="/contact" className="inline-flex items-center gap-3 px-8 py-4 bg-[var(--accent-blue)] text-white rounded-2xl font-bold hover:brightness-110 transition-all shadow-xl shadow-blue-500/20 group">
                Join Us <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Home;